/**
 * WhatsApp message templates per document type.
 *
 * Each entry has:
 *   key:       stable id (used as Firestore doc id under whatsappTemplates/)
 *   docType:   matching docTypeKey for the print engine (or special for masters/reports)
 *   tranType:  the tranTypeCode it relates to (null for masters/reports)
 *   label:     human-readable name shown in Templates UI
 *   recipient: 'customer' | 'vendor' | 'staff' | 'self' — who normally receives this
 *   body:      default message body (supports {variable} placeholders)
 *   attachPdf: default — whether to attach the PDF
 *   enabled:   default — whether the Send button is shown
 *
 * Template variables resolved by `renderTemplate()`:
 *   {customerName}, {vendorName}, {staffName}, {companyName},
 *   {docNumber}, {docDate}, {totalAmount}, {balanceAmount},
 *   {dueDate}, {daysOverdue}, {month}, {year}
 */

export const DEFAULT_TEMPLATES = [
  // ── Sales ─────────────────────────────────────────────────────
  { key: 'saleInvoice', docType: 'saleInvoice', tranType: 'SI', label: 'Sale Invoice', recipient: 'customer',
    body: 'Hi {customerName},\n\nPlease find your invoice {docNumber} dated {docDate} totalling {totalAmount}.\n\nThank you for your business.\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'nonTaxSaleInvoice', docType: 'nonTaxSaleInvoice', tranType: 'NSI', label: 'Non-Tax Sale Invoice', recipient: 'customer',
    body: 'Hi {customerName},\n\nPlease find your invoice {docNumber} dated {docDate} totalling {totalAmount}.\n\nThank you for your business.\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'saleReturn', docType: 'saleReturn', tranType: 'SR', label: 'Sale Return', recipient: 'customer',
    body: 'Hi {customerName},\n\nYour sale return {docNumber} dated {docDate} has been processed for {totalAmount}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'nonTaxSaleReturn', docType: 'nonTaxSaleReturn', tranType: 'NSR', label: 'Non-Tax Sale Return', recipient: 'customer',
    body: 'Hi {customerName},\n\nYour sale return {docNumber} dated {docDate} has been processed for {totalAmount}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'quotation', docType: 'quotation', tranType: 'QT', label: 'Quotation', recipient: 'customer',
    body: 'Hi {customerName},\n\nPlease find quotation {docNumber} dated {docDate} for {totalAmount}.\n\nValid until {dueDate}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'saleOrder', docType: 'saleOrder', tranType: 'SO', label: 'Sale Order', recipient: 'customer',
    body: 'Hi {customerName},\n\nYour order {docNumber} dated {docDate} has been confirmed for {totalAmount}.\n\nWe will deliver shortly.\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'deliveryChallan', docType: 'deliveryChallan', tranType: 'DC', label: 'Delivery Challan', recipient: 'customer',
    body: 'Hi {customerName},\n\nYour order is on the way. Delivery challan {docNumber} dated {docDate}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'posSale', docType: 'saleInvoice', tranType: 'PS', label: 'POS Receipt', recipient: 'customer',
    body: 'Hi {customerName},\n\nThank you for your purchase! Receipt {docNumber} - {totalAmount}.\n\n{companyName}',
    attachPdf: true, enabled: true },

  // ── Purchases (sent to vendors) ───────────────────────────────
  { key: 'purchaseOrder', docType: 'purchaseOrder', tranType: 'PO', label: 'Purchase Order', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nPlease find our purchase order {docNumber} dated {docDate} for {totalAmount}.\n\nKindly confirm.\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'grn', docType: 'grn', tranType: 'GRN', label: 'Goods Receipt (GRN)', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nGoods receipt note {docNumber} dated {docDate} for items received against your delivery.\n\n{companyName}',
    attachPdf: true, enabled: false },
  { key: 'purchaseInvoice', docType: 'purchaseInvoice', tranType: 'PI', label: 'Purchase Invoice (confirmation)', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nWe have recorded your invoice {docNumber} dated {docDate} for {totalAmount} in our books.\n\n{companyName}',
    attachPdf: true, enabled: false },
  { key: 'nonTaxPurchaseInvoice', docType: 'nonTaxPurchaseInvoice', tranType: 'NPI', label: 'Non-Tax Purchase Invoice', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nWe have recorded your invoice {docNumber} dated {docDate} for {totalAmount} in our books.\n\n{companyName}',
    attachPdf: true, enabled: false },
  { key: 'purchaseReturn', docType: 'purchaseReturn', tranType: 'PR', label: 'Purchase Return', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nPurchase return {docNumber} dated {docDate} for {totalAmount} has been processed.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'nonTaxPurchaseReturn', docType: 'nonTaxPurchaseReturn', tranType: 'NPR', label: 'Non-Tax Purchase Return', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nPurchase return {docNumber} dated {docDate} for {totalAmount} has been processed.\n\n{companyName}',
    attachPdf: true, enabled: true },

  // ── Receipts / Payments ───────────────────────────────────────
  { key: 'cashReceipt', docType: 'cashReceipt', tranType: 'CR', label: 'Cash Receipt', recipient: 'customer',
    body: 'Hi {customerName},\n\nWe have received your payment of {totalAmount} on {docDate}. Receipt # {docNumber}.\n\nThank you!\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'bankReceipt', docType: 'bankReceipt', tranType: 'BR', label: 'Bank Receipt', recipient: 'customer',
    body: 'Hi {customerName},\n\nWe have received your bank payment of {totalAmount} on {docDate}. Receipt # {docNumber}.\n\nThank you!\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'cashPayment', docType: 'cashPayment', tranType: 'CP', label: 'Cash Payment', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nWe have made a payment of {totalAmount} on {docDate}. Voucher # {docNumber}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'bankPayment', docType: 'bankPayment', tranType: 'BP', label: 'Bank Payment', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nWe have transferred {totalAmount} to your bank account on {docDate}. Voucher # {docNumber}.\n\n{companyName}',
    attachPdf: true, enabled: true },

  // ── Masters / Reports ─────────────────────────────────────────
  { key: 'customerStatement', docType: 'statement', tranType: null, label: 'Customer Statement', recipient: 'customer',
    body: 'Hi {customerName},\n\nPlease find your account statement as of {docDate}. Outstanding balance: {balanceAmount}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'vendorStatement', docType: 'statement', tranType: null, label: 'Vendor Statement', recipient: 'vendor',
    body: 'Dear {vendorName},\n\nPlease find your account statement as of {docDate}. Outstanding balance: {balanceAmount}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'salaryPayslip', docType: 'payslip', tranType: 'PAY', label: 'Salary Payslip', recipient: 'staff',
    body: 'Dear {staffName},\n\nPlease find your payslip for {month} {year}. Net amount: {totalAmount}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'finalSettlement', docType: 'payslip', tranType: 'FNF', label: 'Final Settlement', recipient: 'staff',
    body: 'Dear {staffName},\n\nPlease find your final settlement {docNumber}. Net amount: {totalAmount}.\n\n{companyName}',
    attachPdf: true, enabled: true },
  { key: 'ledger', docType: 'statement', tranType: null, label: 'Account Ledger', recipient: 'customer',
    body: 'Hi {customerName},\n\nPlease find your ledger for the period.\n\n{companyName}',
    attachPdf: true, enabled: true },

  // ── Reminders ─────────────────────────────────────────────────
  { key: 'paymentReminder', docType: null, tranType: null, label: 'Payment Reminder (auto-cron)', recipient: 'customer',
    body: 'Hi {customerName},\n\nReminder: invoice {docNumber} ({totalAmount}) is overdue by {daysOverdue} days. Please clear at your earliest.\n\nThank you,\n{companyName}',
    attachPdf: false, enabled: true },
];

// Lookup by key
export const TEMPLATE_BY_KEY = {};
DEFAULT_TEMPLATES.forEach(t => { TEMPLATE_BY_KEY[t.key] = t; });

// Replace {variable} placeholders in a template body.
export function renderTemplate(body, vars) {
  if (!body) return '';
  return String(body).replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k];
    return v === undefined || v === null ? '' : String(v);
  });
}
